import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { useICCCColors } from "./theme";

const Card = ({ title, subtitle, value, pct, color, bg, icon, loading, accent, colors }) => (
  <MDBox
    sx={{
      bgcolor: bg || colors.cardBg,
      border: `1px solid ${colors.cardBorder}`,
      borderTop: `4px solid ${accent || color}`,
      borderRadius: 1.5,
      px: 2, py: 1.75,
      display: "flex", alignItems: "center", gap: 2,
      boxShadow: colors.shadow,
      minHeight: 96,
    }}
  >
    <MDBox
      sx={{
        width: 52, height: 52, borderRadius: 1,
        bgcolor: `${color}26`, color,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 28 }}>{icon}</Icon>
    </MDBox>
    <MDBox sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
      <MDTypography
        variant="caption"
        fontWeight="medium"
        sx={{ color: colors.textSecondary, fontSize: 12, display: "block", lineHeight: 1.2 }}
      >
        {title}
      </MDTypography>
      {loading ? (
        <Skeleton width={90} height={36} sx={{ mx: "auto", bgcolor: colors.hoverBg }} />
      ) : (
        <MDTypography
          variant="h4"
          fontWeight="bold"
          sx={{ lineHeight: 1.1, color: colors.textPrimary, my: 0.25 }}
        >
          {Number(value || 0).toLocaleString("en-IN")}
        </MDTypography>
      )}
      {pct !== undefined && (
        <MDTypography variant="caption" sx={{ color: colors.textMuted, fontSize: 11 }}>
          ({pct.toFixed(1)}%)
        </MDTypography>
      )}
      {subtitle && (
        <MDTypography variant="caption" sx={{ color: colors.textDim, fontSize: 10, display: "block" }}>
          {subtitle}
        </MDTypography>
      )}
    </MDBox>
  </MDBox>
);

export default function KPICards({ rollup, loading }) {
  const ICCC_COLORS = useICCCColors();
  const total = rollup?.total || 0;
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card
          title="Total Devices"
          subtitle="Across Groups"
          value={total}
          color={ICCC_COLORS.accent}
          accent={ICCC_COLORS.accent}
          icon="dashboard"
          loading={loading}
          colors={ICCC_COLORS}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card
          title="Healthy Devices"
          value={rollup?.healthy}
          pct={pct(rollup?.healthy || 0)}
          color={ICCC_COLORS.healthy}
          accent={ICCC_COLORS.healthy}
          icon="favorite"
          loading={loading}
          colors={ICCC_COLORS}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card
          title="Warning Devices"
          value={rollup?.warning}
          pct={pct(rollup?.warning || 0)}
          color={ICCC_COLORS.warning}
          accent={ICCC_COLORS.warning}
          icon="warning_amber"
          loading={loading}
          colors={ICCC_COLORS}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card
          title="Critical Devices"
          value={rollup?.critical}
          pct={pct(rollup?.critical || 0)}
          color={ICCC_COLORS.critical}
          accent={ICCC_COLORS.critical}
          bg={ICCC_COLORS.criticalBg}
          icon="cancel"
          loading={loading}
          colors={ICCC_COLORS}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card
          title="Unknown Devices"
          value={rollup?.unknown}
          pct={pct(rollup?.unknown || 0)}
          color={ICCC_COLORS.unknown}
          accent={ICCC_COLORS.unknown}
          icon="help_outline"
          loading={loading}
          colors={ICCC_COLORS}
        />
      </Grid>
    </Grid>
  );
}

Card.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  value: PropTypes.number,
  pct: PropTypes.number,
  color: PropTypes.string,
  bg: PropTypes.string,
  icon: PropTypes.string,
  loading: PropTypes.bool,
  accent: PropTypes.string,
  colors: PropTypes.object,
};

KPICards.propTypes = {
  rollup: PropTypes.shape({
    total: PropTypes.number,
    healthy: PropTypes.number,
    warning: PropTypes.number,
    critical: PropTypes.number,
    unknown: PropTypes.number,
  }),
  loading: PropTypes.bool,
};
